import { getAccessPolicies } from '../../clients/shell/getAccessPolicies';
import { UserContextEnum } from '../../interface/types';
import { IAuthProviderService } from '../authProviderService';
import { IAuthTokenService } from '../authTokenService';
import userContextEnumToAuthContextEnum from '../authTokenService/utils/userContextEnumToAuthContextEnum';
import {
  AccessControlInputType,
  SetServiceDependencies
} from '../../infra/startup/types';
import { IScopeService } from './IScopeService';
import { ScopeRepository } from './ScopeRepository';
import { Scope } from './types';
import IApplicationService from '../applicationService/IApplicationService';
import { ISessionService } from '../session';
import { AccessControlOptionsType } from '../../interface/v1/accessControl/types';
import { InternalService } from '../../core/services';
import { AuthPluginFacade } from '../JWeb/facades/AuthPluginFacade';

export class ScopeService extends InternalService implements IScopeService {
  private _sessionTokensExist = false;
  private _scopeRepository: ScopeRepository;
  private _authProviderService: IAuthProviderService;
  private _authTokenService: IAuthTokenService;
  private _applicationService: IApplicationService;
  private _sessionService: ISessionService;

  constructor() {
    super();
  }

  public setDependencies({ services, repositories }: SetServiceDependencies) {
    const {
      sessionService,
      authProviderService,
      authTokenService,
      applicationService
    } = services;
    const { scopeRepository } = repositories;

    this._scopeRepository = scopeRepository;
    this._authProviderService = authProviderService;
    this._authTokenService = authTokenService;
    this._applicationService = applicationService;
    this._sessionService = sessionService;
    this._markAsReady();
  }

  public hasCachedScopes(options): boolean {
    const repositoryKey = this.getRepositoryKey(options);
    const result = this._scopeRepository.findOne(repositoryKey);
    return result?.length > 0;
  }

  public checkScopes(
    scopesToCheck: Scope[],
    options: AccessControlInputType
  ): boolean {
    if (!this._sessionService?.isLoggedIn()) return false;
    const repositoryKey = this.getRepositoryKey(options);
    const scopes = this._scopeRepository.findOne(repositoryKey);
    return scopesToCheck?.every?.((requestedScope) =>
      scopes?.some?.(
        (cachedScope: Scope) => requestedScope.scope === cachedScope.scope
      )
    );
  }

  // TODO: Refactor to work with AuthContextEnum instead of userContextEnum when AuthContextEnum authProvider exists
  private async getAccessPoliciesScopes(userContextString): Promise<Scope[]> {
    const userContext = userContextString
      ? UserContextEnum[userContextString]
      : null;
    const response = await getAccessPolicies(
      this._authProviderService.createAuthProviderByUserContextEnum(
        userContext
      ),
      this._applicationService
    );
    return (
      response?.scopes.map((scopeName) => {
        return {
          scope: scopeName
        };
      }) || []
    );
  }

  public async updateScopes(
    options: AccessControlOptionsType
  ): Promise<boolean> {
    if (!this._sessionService.isLoggedIn()) return false;

    // In fallback state, do not attempt to fetch new scope validations
    if (AuthPluginFacade.IS_COMMONS_IN_FALLBACK_STATE) {
      console.log(
        '@clientos/commons-norhtbound-api - ScopeService - updateScopes - Skipping scopes refresh during fallback state'
      );
      return false;
    }

    const repositoryKey = this.getRepositoryKey(options);
    const cachedScopes: Scope[] = this._scopeRepository.findOne(repositoryKey);
    // TODO: Refactor to work with AuthContextEnum instead of userContextEnum
    const newScopes = await this.getAccessPoliciesScopes(options?.userContext);
    const sameSize = newScopes?.length === cachedScopes?.length;
    const isEqual =
      sameSize && newScopes.toString() === cachedScopes.toString();
    if (!isEqual) {
      this._scopeRepository.save(repositoryKey, newScopes);
      return true;
    }
    return false;
  }

  public convertToScopes(policies: string[]): Scope[] {
    return policies.map((policy) => ({
      scope: policy
    }));
  }

  // TODO: Need to be removed in the future and this service should work only with AuthContextEnum
  private getRepositoryKey(options): string {
    const userContext = options?.userContext;
    const authContext = userContextEnumToAuthContextEnum(userContext);
    return this._authTokenService.getSuffix(authContext);
  }
}
